import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { MapPin, Plane, Building2, Car, Home, Sparkles, FileText, CreditCard, Briefcase } from "lucide-react";
import { HelpTooltip, NestPointsHelp } from "@/components/ui/help-tooltip";
import RemoteWorkRequestDialog from "./RemoteWorkRequestDialog";

interface Employee {
  id: string;
  company_id: string;
  current_country?: string;
  companies: {
    name: string;
  };
}

interface Profile {
  first_name: string;
  last_name: string;
  residence_country?: string;
}

interface Points {
  total_points: number;
  used_points: number;
}

interface FeaturedDestination {
  id: string;
  name: string;
  photo_url?: string;
  points_required: number;
  description?: string;
  category: string;
}

interface HomeTabProps {
  employee: Employee | null;
  profile: Profile | null;
  currentPoints: Points | null;
  destinations: FeaturedDestination[];
  onRequestSubmitted: () => void;
}

const HomeTab = ({ employee, profile, currentPoints, destinations, onRequestSubmitted }: HomeTabProps) => {
  const services = [
    { name: "Obtention visa", icon: FileText },
    { name: "Aide à l'installation", icon: MapPin },
    { name: "Compte bancaire", icon: CreditCard },
    { name: "Trouver un logement", icon: Home },
    { name: "Conseil fiscal", icon: Briefcase }
  ];

  const travelOptions = [
    { name: "Louer mon appart", partner: "Rehome", icon: Home },
    { name: "Louer ma voiture", partner: "Getaround", icon: Car },
    { name: "Nettoyage logement", partner: "Wecasa", icon: Sparkles }
  ];

  return (
    <div className="space-y-6 sm:space-y-8 overflow-x-hidden">
      {/* Welcome Section */}
      <div className="text-center mb-6 sm:mb-8 px-4">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-2 sm:mb-4 leading-tight">
          Bienvenue {profile?.first_name}
        </h1>
        <p className="text-sm sm:text-lg text-muted-foreground flex items-center justify-center gap-2 flex-wrap">
          <Building2 className="h-4 w-4 flex-shrink-0" />
          <span className="break-words">{employee?.companies?.name || 'Votre entreprise'}</span>
        </p>
      </div>

      {/* Points & Location Cards */}
      <div className="grid sm:grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 px-4">
        <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
          <CardHeader className="pb-3 sm:pb-6">
            <CardTitle className="flex items-center gap-2 sm:gap-3 text-lg sm:text-xl">
              <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0"></div>
              <span className="break-words">Solde Nest Points</span>
              <HelpTooltip content={NestPointsHelp.howPointsWork} />
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {currentPoints ? (
              <div className="space-y-4">
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary mb-1">
                    {currentPoints.total_points - currentPoints.used_points}
                  </div>
                  <div className="text-sm text-muted-foreground">Points disponibles</div>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Points utilisés: {currentPoints.used_points}</span>
                  <span>Total: {currentPoints.total_points}</span>
                </div>
                <div className="w-full bg-muted h-2 rounded-full">
                  <div 
                    className="bg-primary h-2 rounded-full transition-all"
                    style={{ 
                      width: `${(currentPoints.used_points / currentPoints.total_points) * 100}%` 
                    }}
                  />
                </div>
              </div>
            ) : (
              <p className="text-muted-foreground">Chargement des points...</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3 sm:pb-6">
            <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
              <MapPin className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
              <span className="break-words">Ma localisation</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-3 sm:space-y-2">
              <div>
                <span className="font-medium text-sm sm:text-base">Pays de résidence:</span>
                <p className="text-muted-foreground text-sm sm:text-base break-words">{profile?.residence_country || 'Non défini'}</p>
              </div>
              <div>
                <span className="font-medium text-sm sm:text-base">Actuellement:</span>
                <p className="text-muted-foreground text-sm sm:text-base break-words">{employee?.current_country || profile?.residence_country || 'Non défini'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* CTA Button - New Dialog Component */}
      <div className="text-center mb-6 sm:mb-8 px-4">
        <RemoteWorkRequestDialog 
          employee={employee}
          profile={profile}
          currentPoints={currentPoints}
          onRequestSubmitted={onRequestSubmitted}
        />
      </div>

      {/* Destinations Carousel */}
      <Card className="mx-4">
        <CardHeader className="pb-3 sm:pb-6">
          <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
            <Plane className="h-4 w-4 sm:h-5 sm:w-5 text-primary flex-shrink-0" />
            <span className="break-words">Destinations tendances</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {destinations.map((destination) => (
              <div key={destination.id} className="group cursor-pointer">
                <div className="relative overflow-hidden rounded-lg aspect-[4/3] mb-3">
                  <img 
                    src={destination.photo_url || 'https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?w=800&auto=format&fit=crop&q=60&fm=jpg'} 
                    alt={destination.name}
                    loading="lazy"
                    decoding="async"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = 'https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?w=800&auto=format&fit=crop&q=60&fm=jpg';
                    }}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-2 left-2 text-white">
                    <h3 className="font-semibold">{destination.name}</h3>
                    <p className="text-sm opacity-90">{destination.points_required} points/mois</p>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground line-clamp-2">{destination.description}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Services Carousel */}
      <Card className="mx-4">
        <CardHeader className="pb-3 sm:pb-6">
          <CardTitle className="text-lg sm:text-xl break-words">Nos options pour voyager l'esprit léger</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 sm:gap-4">
            {services.map((service, index) => (
              <div key={index} className="text-center p-3 sm:p-4 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
                <service.icon className="h-6 w-6 sm:h-8 sm:w-8 text-primary mx-auto mb-2 sm:mb-3" />
                <p className="text-xs sm:text-sm font-medium text-foreground break-words leading-tight">{service.name}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Travel Options */}
      <Card className="mx-4">
        <CardHeader className="pb-3 sm:pb-6">
          <CardTitle className="text-lg sm:text-xl break-words">Prêt pour le grand départ ?</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
            {travelOptions.map((option, index) => (
              <div key={index} className="flex items-center p-3 sm:p-4 rounded-lg border hover:bg-muted/50 transition-colors cursor-pointer">
                <option.icon className="h-6 w-6 sm:h-8 sm:w-8 text-primary mr-2 sm:mr-3 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm sm:text-base break-words">{option.name}</p>
                  <p className="text-xs sm:text-sm text-muted-foreground break-words">via {option.partner}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default HomeTab;
